"use client"

import {SearchBar} from "@/components/ui/searchbar";
import {useContext, useEffect, useRef, useState} from "react";
import {MenuLink} from "@/components/custom/header/mainmenu/menuLink";
import {getSearchString} from "@/api/actions/search";
import {Spinner} from "@/components/ui/spinner";
import {useRouter} from "next/navigation";
import {useQuery} from "@/hooks/useQuery";
import {setTimeout} from "@datadog/browser-core";
import {useSystemSettings} from "@/hooks/useSystemSettings";

interface HeaderSearchBarProps {
    locale: string;
    searchLabels: {
        search: string;
        noResults: string;
    };
}

export function HeaderSearchBar({ locale, searchLabels }: HeaderSearchBarProps) {
    const {unitSystem, scopeSystem} = useSystemSettings();

    const language = locale.split("-")[0];
    const region = locale.split("-")[1];

    const router = useRouter();

    const [searchValue, setSearchValue] = useState("");
    const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>("");
    const [isVisible, setIsVisible] = useState(false);
    const [loading, setLoading] = useState(false);

    const divRef = useRef<HTMLDivElement>(null);

    const { data: searchResult, isLoading } = useQuery({
        queryFn: async () => {
            if(debouncedSearchValue === '') return;
            const data = await getSearchString(language, debouncedSearchValue, 1, 10, region, unitSystem!, scopeSystem!);
            setLoading(false)
            return data;
        },
        queryKey: [language, region, debouncedSearchValue, scopeSystem, unitSystem],
    })

    function handleInput(e: React.ChangeEvent<HTMLInputElement>) {
        setLoading(true);
        setIsVisible(true);
        setSearchValue(e.target.value);
    }

    function handleClickOutside(e: MouseEvent) {
        if(divRef.current && !divRef.current.contains(e.target as Node)) {
            setIsVisible(false);
        }
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if(e.key === "Enter") {
            if(searchResult?.Results && searchResult?.Results[0].DirectRedirect) {
                router.push(searchResult.Results[0].Link);
                setSearchValue('');
            } else {
                setIsVisible(false);
                router.push(`/${locale}/search?searchString=${searchValue}`);
                setSearchValue('');
            }
        }
    }

    useEffect(() => {
        const delayInputTimeoutId = setTimeout(() => {
            setDebouncedSearchValue(searchValue);
            setLoading(false);
        }, 350);
        return () => clearTimeout(delayInputTimeoutId);
    }, [searchValue]);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return <div
        ref={divRef}
        className={"relative col-span-3 lg:col-span-4"}>
        <SearchBar
            label={searchLabels.search}
            onInput={handleInput}
            icon={searchValue !== "" ? "cross" : "search"}
            onIconClick={() => setSearchValue("")}
            onFocus={() => setIsVisible(true)}
            onKeyDown={handleKeyDown}
            inputValue={searchValue}
        />
        {(isLoading || loading) ? (
            <div className={`z-50 absolute flex-col gap-1 bg-white col-span-3 lg:col-span-4 border w-full rounded-md`}>
            <Spinner />
        </div>
        ) : <>
            {(searchResult && searchValue.length > 0) && (
                <div className={`z-50 absolute ${isVisible ? "flex" : "hidden"} flex-col gap-1 bg-white col-span-3 lg:col-span-4 border w-full rounded-md`}>
                    {(searchResult.ResultCount <= 0) ?
                        <div className={"px-2"}>{searchLabels.noResults}</div> :
                        searchResult.Results.map((item, index) =>
                            <MenuLink
                                key={item.Title + index}
                                link={`${item.Link}`}
                                label={item.Title}
                                className="hover:bg-gray-beige-light px-2"
                                setSearchValue={setSearchValue}
                            />
                        )
                    }
                </div>
            )}
        </>}
    </div>
}